/**
 * Admin · Agent Catalog
 * --------------------
 * Canonical list of all 18 first-class agents from the v3.2 Agent BRD §3.
 * Source of truth for the dashboard's "team of 18 agents" claim.
 *
 * Implementation status is reconciled against the actual codebase as of
 * Feb 2026. Update the `status` and `code_path` fields when an agent moves
 * from defined → wired → production.
 *
 * Mounted at /admin/agents. Same auth gate as /admin/dashboard.
 */

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, AlertCircle, XCircle, Wrench } from 'lucide-react';

function decodeJwt(token) {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch (_e) { return null; }
}

// ─── Canonical 18-agent catalog from v3.2 Agent BRD §3 + Build Order §5 ───
const AGENTS = [
  {
    id: 1, name: 'Coordinator Agent',
    expertise: 'Workflow orchestration & release gates',
    role: 'Owns end-to-end workflow, routing, fan-out/fan-in, release gates. The conductor of the entire multi-agent pipeline.',
    build_phase: 1,
    status: 'production',
    code_path: 'v3/orchestration/coordinator.py · V3Coordinator',
  },
  {
    id: 2, name: 'Intent Agent',
    expertise: 'Query intent classification',
    role: 'Classifies user query into 4 intent types (navigation / action / knowledge / comparison) and emits a confidence score.',
    build_phase: 1,
    status: 'production',
    code_path: 'v2/agents/intent_agent.py · IntentAgent',
  },
  {
    id: 3, name: 'Disambiguation Function',
    expertise: 'Clarification when ambiguous',
    role: 'Asks one precise clarifying question when intent confidence is low or risk requires it. Drives Result Accuracy uplift.',
    build_phase: 1,
    status: 'production',
    code_path: 'v2/agents/disambiguation.py · DisambiguationFunction',
  },
  {
    id: 4, name: 'Control Centre Agent',
    expertise: 'Permissions & accuracy controls',
    role: 'Enforces user permissions, query parameters, accuracy controls and data-handling rules. Gate keeper for all data access.',
    build_phase: 1,
    status: 'production',
    code_path: 'v2/agents/control_centre_agent.py · ControlCentreAgent',
  },
  {
    id: 5, name: 'Tool Router',
    expertise: 'Tool selection & execution',
    role: 'Validates schema, permissions, sensitivity; executes tools. The single entry point for any external action (web search, KG lookup, RAG, file ops).',
    build_phase: 2,
    status: 'observability',
    code_path: 'v2/agents/tool_router.py · ToolRouterAgent',
  },
  {
    id: 6, name: 'Data Governance Agent',
    expertise: 'Retention & deletion compliance',
    role: 'Applies retention policies, deletion runbook, and mandatory data separation. Privacy-by-design enforcement layer.',
    build_phase: 2,
    status: 'partial',
    code_path: 'v3/deletion/runbook.py · DeletionRunbook (functional, not class-shaped yet)',
  },
  {
    id: 7, name: 'Guardrail Agent',
    expertise: 'Safety, privacy & release blocking',
    role: 'Enforces safety, privacy, accuracy gates and blocks unsafe releases. Final-line defence against bad outputs reaching users.',
    build_phase: 2,
    status: 'not_built',
    code_path: '— planned',
  },
  {
    id: 8, name: 'Query Planner Agent',
    expertise: 'Route-specific execution plans',
    role: 'Creates route-specific plan and KG/source strategy. Decides which agents and tools to involve for each query type.',
    build_phase: 3,
    status: 'partial',
    code_path: 'query_router.py + v2/agents/query_planner.py (shim wired, full class not wired)',
  },
  {
    id: 9, name: 'Retrieval Agent',
    expertise: 'Local-first candidate retrieval',
    role: 'Retrieves local-first candidates from user docs and permitted web results. Manages source-rank trade-offs.',
    build_phase: 3,
    status: 'partial',
    code_path: 'rag.py + server.py:async_web_search (functions wired, RetrievalAgent class not wired)',
  },
  {
    id: 10, name: 'Ranking Agent',
    expertise: 'Relevance, authority & freshness scoring',
    role: 'Scores candidates on relevance, authority, freshness, diversity and spam risk. Determines what makes it into the evidence pack.',
    build_phase: 3,
    status: 'not_built',
    code_path: 'v2/agents/ranking_agent.py · RankingAgent (defined, not wired)',
  },
  {
    id: 11, name: 'Evidence Agent',
    expertise: 'Evidence pack assembly & contradiction',
    role: 'Builds evidence packs from ranked candidates and flags internal contradictions. Bridges retrieval to drafting.',
    build_phase: 3,
    status: 'not_built',
    code_path: 'v2/agents/evidence_agent.py · EvidenceAgent (defined, not wired)',
  },
  {
    id: 12, name: 'Drafting Agent',
    expertise: 'Evidence-grounded answer generation',
    role: 'Creates evidence-grounded draft responses with mandatory [[n]] citations. The agent users actually "hear" when an answer comes back.',
    build_phase: 4,
    status: 'partial',
    code_path: 'server.py inline drafter (production) · v2/agents/drafting_agent.py (proper class not wired)',
  },
  {
    id: 13, name: 'Verification Agent',
    expertise: 'Citation & claim verification',
    role: 'Blocks unsupported claims and missing citations. Final pre-release check on every drafted answer.',
    build_phase: 4,
    status: 'partial',
    code_path: 'Heuristic check in drafter trace (citation count + grounding) · v2/agents/verification_agent.py (proper LLM verifier not wired)',
  },
  {
    id: 14, name: 'Prompt Suggestion Agent',
    expertise: 'Pre-submission query refinement',
    role: 'Suggests query improvements locally before the user hits send. Reduces ambiguous queries upstream of the pipeline.',
    build_phase: 5,
    status: 'not_built',
    code_path: '— planned',
  },
  {
    id: 15, name: 'Memory Agent',
    expertise: 'Session context & staleness',
    role: 'Manages local-scoped session context, decides when context is stale, and what to carry forward across queries.',
    build_phase: 5,
    status: 'not_built',
    code_path: '— planned',
  },
  {
    id: 16, name: 'Knowledge Graph Intelligence Agent',
    expertise: 'KG ingestion & reasoning',
    role: 'Ingests, links, retrieves and reasons over the user\'s local Knowledge Graph. The brains of the local-first storage layer.',
    build_phase: 5,
    status: 'partial',
    code_path: 'rag.py vector store + v3_vector_embedding_meta (functions wired, no v3 KG agent class)',
  },
  {
    id: 17, name: 'Revenue Share Agent',
    expertise: 'Revenue attribution events',
    role: 'Tracks revenue-share events separately from ranking/answers. Phase 6 — keeps creator economics audit-ready and isolated from quality logic.',
    build_phase: 6,
    status: 'not_built',
    code_path: 'v2/agents/__init__.py · RevenueShareAgent (Phase 6 placeholder, raises NotImplementedError)',
  },
  {
    id: 18, name: 'Evaluation & Learning Agent',
    expertise: 'Scenario tests, root cause, uplift loop',
    role: 'Runs scenario tests, performs root-cause analysis on failures, drives the uplift learning loop. The agent that grades all the others.',
    build_phase: 6,
    status: 'not_built',
    code_path: '— planned (will consume agent_traces + cohesion events)',
  },
];

const STATUS_META = {
  production:   { color: 'text-emerald-400', bg: 'bg-emerald-950/40 border-emerald-900', icon: CheckCircle2, label: 'Production' },
  observability:{ color: 'text-cyan-400',    bg: 'bg-cyan-950/40 border-cyan-900',       icon: Wrench,        label: 'Observability only' },
  partial:      { color: 'text-amber-400',   bg: 'bg-amber-950/40 border-amber-900',     icon: AlertCircle,   label: 'Partial' },
  not_built:    { color: 'text-slate-400',   bg: 'bg-slate-900/40 border-slate-800',     icon: XCircle,       label: 'Not yet built' },
};

const PHASE_NAME = {
  1: 'Phase 1 · Foundation',
  2: 'Phase 2 · Permissions & Tools',
  3: 'Phase 3 · Retrieval Layer',
  4: 'Phase 4 · Drafting & Verification',
  5: 'Phase 5 · Local-First Intelligence',
  6: 'Phase 6 · Revenue & Learning',
};

export default function AdminAgents() {
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('startech_token');
    if (!token) { navigate('/'); return; }
    const claims = decodeJwt(token);
    if (!claims || claims.sub !== 'startech') navigate('/');
  }, [navigate]);

  const counts = {
    total: AGENTS.length,
    production: AGENTS.filter((a) => a.status === 'production').length,
    observability: AGENTS.filter((a) => a.status === 'observability').length,
    partial: AGENTS.filter((a) => a.status === 'partial').length,
    not_built: AGENTS.filter((a) => a.status === 'not_built').length,
  };

  const phases = [...new Set(AGENTS.map((a) => a.build_phase))].sort();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 to-slate-900 text-slate-200 p-8" data-testid="admin-agents">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-cyan-400 tracking-tight" data-testid="admin-agents-title">
              Search.Inc · Agent Catalog
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              {counts.total} first-class agents per v3.2 Agent BRD §3 · Build progress as of Feb 2026
            </p>
          </div>
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 rounded-lg text-sm border border-slate-700"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft size={14} /> Back to dashboard
          </button>
        </div>

        {/* Build progress strip */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
          <div className="p-4 rounded-xl border border-cyan-900/40 bg-slate-900/70" data-testid="agent-count-total">
            <div className="text-3xl font-bold text-cyan-400 tabular-nums">{counts.total}</div>
            <div className="text-xs text-slate-400 uppercase tracking-wider mt-1">Total agents</div>
          </div>
          <div className="p-4 rounded-xl border border-emerald-900 bg-emerald-950/20">
            <div className="text-3xl font-bold text-emerald-400 tabular-nums">{counts.production}</div>
            <div className="text-xs text-slate-400 uppercase tracking-wider mt-1">Production</div>
          </div>
          <div className="p-4 rounded-xl border border-cyan-900 bg-cyan-950/20">
            <div className="text-3xl font-bold text-cyan-400 tabular-nums">{counts.observability}</div>
            <div className="text-xs text-slate-400 uppercase tracking-wider mt-1">Observability only</div>
          </div>
          <div className="p-4 rounded-xl border border-amber-900 bg-amber-950/20">
            <div className="text-3xl font-bold text-amber-400 tabular-nums">{counts.partial}</div>
            <div className="text-xs text-slate-400 uppercase tracking-wider mt-1">Partial</div>
          </div>
          <div className="p-4 rounded-xl border border-slate-800 bg-slate-900/40">
            <div className="text-3xl font-bold text-slate-400 tabular-nums">{counts.not_built}</div>
            <div className="text-xs text-slate-400 uppercase tracking-wider mt-1">Not yet built</div>
          </div>
        </div>

        {/* Agent list grouped by build phase */}
        {phases.map((phase) => (
          <section key={phase} className="mb-10" data-testid={`phase-${phase}`}>
            <h2 className="text-xs uppercase tracking-widest text-cyan-400 font-semibold mb-3 pb-2 border-b border-slate-800">
              {PHASE_NAME[phase] || `Phase ${phase}`}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {AGENTS.filter((a) => a.build_phase === phase).map((agent) => {
                const meta = STATUS_META[agent.status];
                const StatusIcon = meta.icon;
                return (
                  <div
                    key={agent.id}
                    className={`p-5 rounded-xl border ${meta.bg} backdrop-blur-sm`}
                    data-testid={`agent-card-${agent.id}`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div>
                        <div className="text-[10px] text-slate-500 font-mono">#{String(agent.id).padStart(2, '0')}</div>
                        <h3 className="text-base font-semibold text-slate-100">{agent.name}</h3>
                        <p className="text-xs text-cyan-400 mt-0.5">{agent.expertise}</p>
                      </div>
                      <span className={`flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold ${meta.color}`}>
                        <StatusIcon size={12} /> {meta.label}
                      </span>
                    </div>
                    <p className="text-sm text-slate-300 leading-relaxed mt-3">{agent.role}</p>
                    <p className="text-[10px] text-slate-500 font-mono mt-3 truncate" title={agent.code_path}>
                      {agent.code_path}
                    </p>
                  </div>
                );
              })}
            </div>
          </section>
        ))}

        <div className="mt-10 pt-6 border-t border-slate-800 text-xs text-slate-500">
          Source: SearchInc_Agent_BRD_v3_2.docx · §3 Agent Catalog · v3.2 Controlled Baseline · April 28, 2026
        </div>
      </div>
    </div>
  );
}
